import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";

/**
 * A panel that rises from the bottom edge, for short choices on a phone.
 *
 * Built on the same Radix dialog as every other overlay here, so focus,
 * Escape and nesting inside another dialog behave the same way. Unlike a
 * popover it does not close when the window resizes, which is what the
 * keyboard hiding does on Android.
 */
export const BottomSheet = DialogPrimitive.Root;
export const BottomSheetTrigger = DialogPrimitive.Trigger;

export const BottomSheetContent = forwardRef(function BottomSheetContent(
  { className, children, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <DialogPrimitive.Content
        ref={ref}
        // Radix moves focus into the sheet on open. That is load-bearing: it
        // takes focus off any text field, so the keyboard goes away instead of
        // covering the list.
        //
        // The title says all there is to say; no separate description.
        aria-describedby={undefined}
        className={cn(
          "fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col rounded-t-xl border border-b-0 border-border bg-card pt-2 shadow-lg",
          "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          className,
        )}
        {...props}
      >
        <div className="mx-auto mb-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30" aria-hidden="true" />
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

export function BottomSheetTitle({ className, ...props }) {
  return (
    <DialogPrimitive.Title
      className={cn("px-5 pb-2 font-heading text-base font-bold tracking-tight", className)}
      {...props}
    />
  );
}
